import { useState } from 'react'
import dynamic from 'next/dynamic'
import { useRouter } from 'next/router'
import PropTypes from 'prop-types'
import { LOGIN, REGISTER, CONFIRM, RESET } from '@mc/authentication'
import { paths } from '@mc/paths'
import { homePath } from 'libs/paths'
import { Meta } from './components/Meta'

const mapMethod = (method) => {
  switch (method) {
    case 'register':
      return REGISTER
    case 'reset-password':
      return RESET
    case 'confirm-email':
      return CONFIRM
    default:
      return LOGIN
  }
}

const Authentication = dynamic(
  () => import('@mc/authentication').then((modules) => modules.Authentication),
  {
    ssr: false,
  },
)

const Login = ({ userAgent, method }) => {
  const router = useRouter()
  const [view, setView] = useState(method)

  const handleShow = (newView) => () => setView(newView)

  const handleClose = () => {
    router.push(homePath())
  }

  const handleLogIn = () => {
    // in nextjs send to root instead of default reload behavior
    window.location.href = '/'
  }

  return (
    <>
      <Meta view={view} />

      <Authentication
        view={view}
        show={handleShow}
        close={handleClose}
        onLogIn={handleLogIn}
        nextPage={router.query.next_page}
        userAgent={userAgent}
      />
    </>
  )
}

export async function getServerSideProps(ctx) {
  /**
   * It seems that without a `gSSP` method exported, NextJS will get
   * confused and try to render the client side transition statically.
   * It renders our `_app.page.js` layout and does not pass in a `req`
   * so `getInitialProps` does not have a request to fetch data from.
   * This causes `experiments` to be undefined, and it causes this page
   * to error when being rendered. But this only happens during a
   * client-side transition for some reason.
   */
  const {
    req: {
      headers: { 'user-agent': userAgent },
      cookies: { _mcl: isLoggedIn },
    },
    query: {
      method: _method,
      require_signin: requireSignIn,
      next_page: nextPage,
    },
  } = ctx

  const method = mapMethod(_method)

  if (!!isLoggedIn && requireSignIn && method === LOGIN) {
    return {
      redirect: {
        destination: nextPage || paths.root,
        permanent: false,
      },
    }
  }

  return {
    props: {
      userAgent,
      method,
    },
  }
}

Login.pageSettings = {
  analyticsLabel: 'Login',
}

Login.propTypes = {
  userAgent: PropTypes.string,
  method: PropTypes.oneOf([REGISTER, RESET, CONFIRM, LOGIN]),
}

export default Login
